import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { gameInPlay, gamesOf, nextSabotageAt, stopOf } from '@continuum/show-core';
import type { GuestIntent } from '@continuum/show-core';
import type { WireState, You } from '../room/client.js';
import { Hero, PrimaryButton, mono, sans } from '../ui/atoms.js';
import { DialBoard } from './hack/Dial.js';
import { TraceBoard } from './hack/Trace.js';
import { clockKey, useGameClock } from './clock.js';
import { armedOf } from '../ui/you.js';

/** The screen's one line to read, as big as a title. */
const HEADLINE = { fontSize: 34, fontWeight: 800, lineHeight: 1.05 } as const;
import { ROUND_COPY } from '../ui/roundText.js';
import type { Lang } from '../ui/text.js';

/**
 * The saboteur's own game (Marek, 2026-09-28: "saboteurs get their own game"), in three versions,
 * since a guest can be drawn at several stops and nobody plays the same game twice in an evening
 * (Marek, 2026-09-29). Each is easy, half a minute to a minute:
 *   0. the wires: match four wires to their sockets, then type a four-digit code;
 *   1. the circuit: tap six numbered dots from 1 to 6, then type the code (hack/Trace.tsx);
 *   2. the lock: turn a safe's dial to three numbers in turn, and it opens (hack/Dial.tsx).
 * START deals the first version this phone has not played tonight — kept in localStorage under the
 * voyage id, beside the evening it belongs to — and once all three are played, the one played
 * longest ago. With no storage, the stop picks: (chapter − 1) % 3. A version counts as played once
 * it is solved, sent or not. `variant` forces one (the practice page shows each in turn).
 *
 * It is the saboteur's game of the stop (Marek, 2026-10-06): it stands in their games list in place
 * of the games dealt to them, and PLAY NOW opens it. Solving it is the sabotage: the phone sends
 * `sabotage` with its time on screen and its mistakes, like any game, the room arms the alarm and
 * finishes the games dealt to this guest, and the alarm goes off once everyone's games are done.
 *
 * To every other screen it is an ordinary game: while it is played the phone sends progress and a
 * thirty game's frame (`r:<round>|b:0`) for the first game dealt to this guest, so the table, the TV
 * and anyone watching see that game moving. What it really is never leaves the phone.
 */

const WIRES = [
  { colour: '#ff4d4d', shape: '●' },
  { colour: '#ffd400', shape: '▲' },
  { colour: '#3fa9ff', shape: '■' },
  { colour: '#4fe07a', shape: '◆' },
] as const;

type Stage = 'intro' | 'wires' | 'trace' | 'dial' | 'code' | 'sent' | 'done';

/** The versions, in the order a phone meets them. */
export type HackVersion = 0 | 1 | 2;
const VERSIONS: readonly HackVersion[] = [0, 1, 2];
const FIRST_STAGE: Record<HackVersion, Stage> = { 0: 'wires', 1: 'trace', 2: 'dial' };
const isVersion = (v: unknown): v is HackVersion => VERSIONS.includes(v as HackVersion);

/** The first version not played yet; once all are, the one played longest ago. `played` is oldest first. */
export function nextVersion(played: readonly number[]): HackVersion {
  return VERSIONS.find((v) => !played.includes(v)) ?? played.find(isVersion) ?? 0;
}

/** The list once `v` is played: it goes to the end, as the one played last. */
export function playedAfter(played: readonly number[], v: HackVersion): HackVersion[] {
  return [...played.filter((x): x is HackVersion => isVersion(x) && x !== v), v];
}

/** With nothing remembered, the stop picks: the first stop the first version, and round again. */
export function versionForStop(chapter: number): HackVersion {
  const n = VERSIONS.length;
  return VERSIONS[(((chapter - 1) % n) + n) % n]!;
}

type Store = Pick<Storage, 'getItem' | 'setItem'>;

export const hackKey = (voyageId: string) => `continuum.hack.${voyageId}`;

/**
 * The versions this phone has played tonight, oldest first; null when there is no storage to keep
 * them in. The room is always the same one, so the evening is kept beside the list, and a new
 * evening starts from the first version again.
 */
export function readPlayed(store: Store | null, voyageId: string, evening: string): HackVersion[] | null {
  if (!store) return null;
  let raw: string | null;
  try {
    raw = store.getItem(hackKey(voyageId));
    // Written straight back: a phone that can read but not write remembers nothing either.
    store.setItem(hackKey(voyageId), raw ?? JSON.stringify({ evening, played: [] }));
  } catch {
    return null;
  }
  try {
    const kept = raw ? (JSON.parse(raw) as { evening?: unknown; played?: unknown }) : null;
    return kept?.evening === evening && Array.isArray(kept.played) ? kept.played.filter(isVersion) : [];
  } catch {
    return [];
  }
}

/** Which version START deals tonight (the comment above). */
export function pickVersion(store: Store | null, voyageId: string, evening: string, chapter: number): HackVersion {
  const played = readPlayed(store, voyageId, evening);
  return played ? nextVersion(played) : versionForStop(chapter);
}

/** A version solved: the next START, at this stop or a later one, deals another. */
export function rememberPlayed(store: Store | null, voyageId: string, evening: string, v: HackVersion): void {
  const played = readPlayed(store, voyageId, evening);
  if (!store || !played) return;
  try {
    store.setItem(hackKey(voyageId), JSON.stringify({ evening, played: playedAfter(played, v) }));
  } catch {
    /* nothing kept: the next START deals as if this one was never played */
  }
}

/** This phone's storage, if the browser gives the page any. */
function phoneStore(): Store | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** The evening a room is on: its day and tonight's launch, which "Start a new evening" both renew. */
const eveningOf = (state: WireState) => `${state.date}.${state.launchedAt ?? 0}`;

function useNow(ms = 500): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

/** A fresh order for the sockets and a fresh code, every time the game starts. */
function deal(): { sockets: number[]; code: string } {
  const sockets = [0, 1, 2, 3];
  for (let i = sockets.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [sockets[i], sockets[j]] = [sockets[j]!, sockets[i]!];
  }
  // Never the plain order: that would be no puzzle at all.
  if (sockets.every((s, i) => s === i)) sockets.reverse();
  const code = String(1000 + Math.floor(Math.random() * 9000));
  return { sockets, code };
}

export function Hack({
  lang,
  state,
  you,
  send,
  privateNote,
  practice,
  variant,
  paused = false,
}: {
  lang: Lang;
  state?: WireState;
  you?: You;
  send?: (intent: GuestIntent) => void;
  /** The room's word on a turned-down sabotage, which only this phone sees. */
  privateNote?: { text: string; at: number } | null;
  /**
   * The practice page (practice.tsx): no room, nothing sent, never locked — solving it just calls
   * this. A real saboteur's phone never passes it.
   */
  practice?: () => void;
  /** Forces a version instead of dealing one; nothing is remembered for it. */
  variant?: HackVersion;
  /** Something covers the game (an alarm, a sheet): the stopwatch waits. */
  paused?: boolean;
}) {
  const r = ROUND_COPY[lang];
  const now = useNow(500);
  // Played already at this stop: it says so, and its alarm waits for the end of the games.
  const [stage, setStage] = useState<Stage>(() => (!practice && armedOf(you, state) ? 'done' : 'intro'));
  const [version, setVersion] = useState<HackVersion>(variant ?? 0);
  const [board, setBoard] = useState(deal);
  const [picked, setPicked] = useState<number | null>(null);
  const [joined, setJoined] = useState<number[]>([]);
  const [typed, setTyped] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const sentAt = useRef<{ at: number } | null>(null);

  // Whether a sabotage can still be armed, from what the room already shows every phone: during the
  // stop's games, before its end has begun.
  const readyAt = state ? nextSabotageAt(state, now) : null;
  const inGames = state ? gameInPlay(state) && state.phase === 'fieldwork' : false;
  const blocked =
    practice || !state || !you
      ? null
      : you.brig
        ? r.hackNone
        : !inGames
          ? r.hackNotNow
          : readyAt === null || stopOf(state)
            ? r.hackBusy
            : you.sabotagesLeft <= 0
              ? r.hackNone
              : null;

  // The game it stands in for on every other screen: the first one dealt to this guest.
  const cover = !practice && state && you ? (gamesOf(state, state.chapter, you.seat)[0] ?? null) : null;
  const playing = stage === 'wires' || stage === 'trace' || stage === 'dial' || stage === 'code';
  // Its time on screen and its mistakes, kept like any game's (games/clock.ts).
  const stopwatch = useGameClock(playing && !practice && state && you ? clockKey(state.voyageId, state.chapter, -1, you.seat) : null, paused);
  const shown = useRef(0);
  /** Progress and a frame for the cover game, forward only, on a new whole percent. */
  const report = (percent: number, round: number) => {
    if (!send || !state || cover === null) return;
    const progress = Math.max(1, Math.min(99, Math.round(percent)));
    if (progress <= shown.current) return;
    shown.current = progress;
    send({ type: 'taskProgress', chapter: state.chapter, index: cover, progress });
    send({ type: 'taskFrame', chapter: state.chapter, index: cover, frame: `r:${round}|b:0` });
  };
  const slip = () => stopwatch?.mistake();

  // It worked once the room has counted it; if the room said no, why.
  useEffect(() => {
    if (!you || practice) return;
    if (armedOf(you, state) && stage !== 'done') {
      if (stage === 'sent') navigator.vibrate?.(40);
      setStage('done');
      return;
    }
    const sent = sentAt.current;
    if (stage === 'sent' && sent && privateNote && privateNote.at >= sent.at) {
      setNote(privateNote.text);
      setStage('intro');
    }
  }, [stage, armedOf(you, state), privateNote]);

  // Only a real saboteur's phone remembers what it played; practice and a forced version never do.
  const remembers = variant === undefined && !practice && state ? state : null;

  const start = () => {
    const v = variant ?? (remembers ? pickVersion(phoneStore(), remembers.voyageId, eveningOf(remembers), remembers.chapter) : 0);
    setVersion(v);
    setBoard(deal());
    setPicked(null);
    setJoined([]);
    setTyped('');
    setNote(null);
    setStage(FIRST_STAGE[v]);
    report(2, 1);
  };

  const socket = (wire: number) => {
    if (picked === null) return;
    if (wire === picked) {
      const next = [...joined, wire];
      setJoined(next);
      setPicked(null);
      setNote(null);
      report(8 + next.length * 12, 1);
      if (next.length === WIRES.length) toCode();
    } else {
      setPicked(null);
      setNote(r.hackWrong);
      slip();
    }
  };

  const toCode = () => {
    setStage('code');
    report(60, 2);
  };

  // Solved, whichever the version: the sabotage, if it can still go, with its time and mistakes.
  const solve = () => {
    if (remembers) rememberPlayed(phoneStore(), remembers.voyageId, eveningOf(remembers), version);
    if (blocked) {
      setNote(blocked);
      setStage('intro');
      return;
    }
    if (practice || !you || !send) {
      setStage('done');
      practice?.();
      return;
    }
    const run = stopwatch?.read(Date.now()) ?? null;
    sentAt.current = { at: Date.now() };
    send({ type: 'sabotage', ...(run ? { ms: run.ms, mistakes: run.mistakes } : {}) });
    setStage('sent');
  };

  const key = (digit: string) => {
    const next = `${typed}${digit}`.slice(0, 4);
    setTyped(next);
    if (next.length < 4) {
      if (board.code.startsWith(next)) report(60 + next.length * 8, 2);
      return;
    }
    if (next !== board.code) {
      setNote(r.hackWrong);
      setTyped('');
      slip();
      return;
    }
    solve();
  };

  const shownCode = useMemo(() => board.code.split('').join(' '), [board.code]);

  if (stage === 'done') {
    return (
      <Frame>
        <Hero style={HEADLINE}>{r.hackDone}</Hero>
      </Frame>
    );
  }

  if (stage === 'sent') {
    return (
      <Frame>
        <Hero style={HEADLINE}>{r.hackSending}</Hero>
      </Frame>
    );
  }

  if (stage === 'intro') {
    return (
      <Frame>
        <div style={{ fontFamily: mono, fontSize: 14, lineHeight: 1.5, color: '#ffb3c1' }}>{r.hackSecret}</div>
        <div style={{ flex: 1, minHeight: 24 }} />
        {note ? <div style={{ fontFamily: sans, fontSize: 19, fontWeight: 600, color: '#ffd1da', marginBottom: 14 }}>{note}</div> : null}
        {blocked ? (
          <Hero style={{ ...HEADLINE, fontSize: 28 }}>{blocked}</Hero>
        ) : (
          <PrimaryButton big bg="#ff5f86" fg="#2a0610" onClick={start}>
            {r.hackStart}
          </PrimaryButton>
        )}
      </Frame>
    );
  }

  if (stage === 'trace') {
    return (
      <Frame>
        <TraceBoard r={r} onDone={toCode} onStep={(n) => report(8 + n * 8, 1)} onMistake={slip} />
      </Frame>
    );
  }

  if (stage === 'dial') {
    return (
      <Frame>
        <DialBoard r={r} onDone={solve} onStep={(n) => report(10 + n * 28, n + 1)} onMistake={slip} />
      </Frame>
    );
  }

  if (stage === 'wires') {
    return (
      <Frame>
        <div style={{ fontFamily: sans, fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>{r.hackWires}</div>
        <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 28px' }}>
          {WIRES.map((_, row) => {
            const wire = row;
            const target = board.sockets[row]!;
            const left = WIRES[wire]!;
            const right = WIRES[target]!;
            const wireDone = joined.includes(wire);
            const socketDone = joined.includes(target);
            return [
              <button
                key={`w${wire}`}
                disabled={wireDone}
                onClick={() => setPicked(wire)}
                style={plug(left.colour, picked === wire, wireDone)}
              >
                {left.shape}
              </button>,
              <button key={`s${target}`} disabled={socketDone} onClick={() => socket(target)} style={plug(right.colour, false, socketDone, true)}>
                {right.shape}
              </button>,
            ];
          })}
        </div>
        <div style={{ minHeight: 26, marginTop: 14, fontFamily: sans, fontSize: 18, fontWeight: 600, color: '#ffd1da' }}>{note ?? ''}</div>
      </Frame>
    );
  }

  return (
    <Frame>
      <div style={{ fontFamily: sans, fontSize: 20, fontWeight: 600 }}>{r.hackCode}</div>
      <div style={{ marginTop: 8, fontFamily: mono, fontSize: 44, fontWeight: 700, letterSpacing: 8, color: '#ffffff' }}>{shownCode}</div>
      <div style={{ marginTop: 8, fontFamily: mono, fontSize: 30, letterSpacing: 10, color: '#ffb3c1', minHeight: 40 }}>
        {typed.padEnd(4, '·').split('').join(' ')}
      </div>
      <div style={{ minHeight: 24, fontFamily: sans, fontSize: 18, fontWeight: 600, color: '#ffd1da' }}>{note ?? ''}</div>
      <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((k) =>
          k === '' ? (
            <span key="gap" />
          ) : (
            <button
              key={k}
              onClick={() => (k === '⌫' ? setTyped((t) => t.slice(0, -1)) : key(k))}
              style={{
                minHeight: 60,
                borderRadius: 14,
                border: '1px solid rgba(255,255,255,.2)',
                background: 'rgba(255,255,255,.06)',
                color: '#ffffff',
                fontFamily: mono,
                fontSize: 26,
                fontWeight: 700,
              }}
            >
              {k}
            </button>
          ),
        )}
      </div>
    </Frame>
  );
}

function Frame({ children }: { children: ReactNode }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px 20px 24px', boxSizing: 'border-box', color: '#dfe9f2', overflowY: 'auto' }}>
      {children}
    </div>
  );
}

/** A wire end or a socket: its colour and its shape, so it reads for anyone. */
function plug(colour: string, picked: boolean, done: boolean, socket = false): CSSProperties {
  return {
    minHeight: 64,
    borderRadius: socket ? 32 : 14,
    border: `3px solid ${colour}`,
    background: done ? colour : picked ? `${colour}55` : 'rgba(255,255,255,.04)',
    color: done ? '#05070a' : colour,
    fontSize: 30,
    fontWeight: 700,
    opacity: done ? 0.55 : 1,
    transform: picked ? 'scale(1.04)' : 'none',
    transition: 'transform .15s ease, background-color .2s ease',
  };
}
